import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { Layout } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import PageShell from '../components/PageShell'
import Drawer from '../components/Drawer'
import FormField from '../components/FormField'
import styles from '../components/PanelWidgetCard.module.css'
import { PanelWidgetCard } from '../components/PanelWidgetCard'
import { usePanelWidgetReportRows } from '../hooks/usePanelWidgetReportRows'
import { isDateLikeFieldKey, parseDateGroup } from '../utils/panelWidgetRendering'
import {
  ResponsiveGridLayout,
  GRID_BREAKPOINT_DESKTOP,
  GRID_BREAKPOINTS,
  GRID_COLS,
  GRID_ROW_HEIGHT,
  buildLayoutItem,
  computeChangedLayoutEntries
} from '../utils/panelWidgetGrid'
import { DATASETS } from '../utils/reportDatasets'
import { getLocalizedDataset } from '../utils/reportLabels'
import { offeredColumns } from '../utils/reportColumns'
import {
  loadReports,
  type ReportDefinitionV1
} from '../utils/reportStorage'
import { loadHomeWidgetPins, setHomeWidgetPinned } from '../utils/homeWidgetsStorage'
import { seedDefaultPanelAndHomeContent } from '../utils/panelDefaults'
import { useT } from '../i18n'
import {
  deleteWidget,
  loadDashboard,
  setWidgetLayouts,
  upsertPivotWidget,
  type ChartSeriesMode,
  type ChartType,
  type DashboardDefinitionV1,
  type DateGroup,
  type PivotWidgetV1
} from '../utils/dashboardStorage'
import { PIVOT_AGGREGATIONS, type PivotAggregation } from '../utils/pivot'

// v2.2 findings.md #47: form state for the aggregation-specific inputs
// (see `PivotConfig` in pivot.ts) and the per-column chart series toggle.
// `ratioMultiplier` is the select's string value ('' = none).
type AdvancedWidgetDraft = {
  orderField: string
  entityField: string
  denominatorField: string
  weightField: string
  ratioMultiplier: string
  chartSeries: ChartSeriesMode
}

const EMPTY_ADVANCED_WIDGET_DRAFT: AdvancedWidgetDraft = {
  orderField: '',
  entityField: '',
  denominatorField: '',
  weightField: '',
  ratioMultiplier: '',
  chartSeries: 'total'
}

const hasRequiredAdvancedFields = (draft: AdvancedWidgetDraft & { aggregation: PivotAggregation }): boolean => {
  if (draft.aggregation === 'sumOfLast') return Boolean(draft.entityField)
  if (draft.aggregation === 'ratio') return Boolean(draft.denominatorField)
  if (draft.aggregation === 'weightedAvg') return Boolean(draft.weightField)
  return true
}

export default function NewDashboard() {
  const t = useT()
  const location = useLocation()
  const [dashboard, setDashboard] = useState<DashboardDefinitionV1>(() => loadDashboard())
  const [reports, setReports] = useState<ReportDefinitionV1[]>(() => loadReports())
  // Whether *this* user has pinned each widget to their own /home — a
  // personal selection (see homeWidgetsStorage.ts), separate from the
  // tenant-wide widget definitions in `dashboard`.
  const [homePinnedWidgetIds, setHomePinnedWidgetIds] = useState<Set<string>>(
    () => new Set(loadHomeWidgetPins().map((pin) => pin.widgetId))
  )

  const [widgetDraft, setWidgetDraft] = useState<{
    editingId: string | null
    title: string
    reportId: string
    rowField: string
    columnField: string
    rowDateGroup: '' | DateGroup
    columnDateGroup: '' | DateGroup
    aggregation: PivotAggregation
    valueField: string
    showChart: boolean
    chartType: ChartType
  } & AdvancedWidgetDraft>(() => ({
    editingId: null,
    title: '',
    reportId: reports[0]?.id ?? '',
    rowField: '',
    columnField: '',
    rowDateGroup: '',
    columnDateGroup: '',
    aggregation: 'sum',
    valueField: '',
    showChart: true,
    chartType: 'bar',
    ...EMPTY_ADVANCED_WIDGET_DRAFT
  }))

  // UX polish: operators open this screen to look at their widgets, not to
  // create a new one — the add/edit form is now tucked behind a drawer
  // (closed by default) instead of always sitting above the widget grid.
  const [isWidgetFormOpen, setIsWidgetFormOpen] = useState(false)

  // Reports' "Send to Panel" quick action deep-links here with
  // ?reportId=&openWidgetForm=1 so the widget drawer opens pre-selected
  // to that report instead of the operator re-finding it in the dropdown.
  // Tracked by the exact query string (not just presence) so closing the
  // drawer manually doesn't pop it back open the next time `reports`
  // changes reference for an unrelated reason (e.g. a seed effect).
  const consumedDeepLinkRef = useRef<string | null>(null)
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const requestedReportId = params.get('reportId')?.trim() ?? ''
    const shouldOpen = params.get('openWidgetForm') === '1'
    if (!requestedReportId || !shouldOpen) return
    if (consumedDeepLinkRef.current === location.search) return
    if (!reports.some((report) => report.id === requestedReportId)) return

    consumedDeepLinkRef.current = location.search
    setWidgetDraft((prev) => ({
      ...prev,
      editingId: null,
      title: '',
      reportId: requestedReportId,
      columnField: '',
      rowDateGroup: '',
      columnDateGroup: '',
      valueField: '',
      chartType: 'bar',
      ...EMPTY_ADVANCED_WIDGET_DRAFT
    }))
    setIsWidgetFormOpen(true)
  }, [location.search, reports])

  // Real drag/resize pass: react-grid-layout only tells us which breakpoint
  // is active, not whether it's safe to persist — mobile's single-column
  // layout is auto-generated from the desktop one, not something the
  // operator arranged, so a resize-triggered breakpoint change must never
  // get written back as if it were the desktop layout.
  const [gridBreakpoint, setGridBreakpoint] = useState<string>(GRID_BREAKPOINT_DESKTOP)

  // 2026-09-12 pack (items a+b): Panel is an equally valid first screen as
  // Reports, so it seeds/migrates the same two lot-cost-per-kg reports
  // itself (idempotent — see `seedDefaultReportsIfEmpty`'s own docstring;
  // safe to call from both screens) rather than depending on the operator
  // having visited Reports first, then seeds the matching "FCR by lot"
  // widgets once each report is confirmed present. Runs once on mount.
  useEffect(() => {
    // v2.2 findings.md #48: all default reports/widgets now seed from one
    // shared function (also called by /home), plus this user's one-time
    // default Home pins so the "Fijar en Inicio" state here matches Home
    // whichever screen the user opens first.
    const seeded = seedDefaultPanelAndHomeContent(t)
    setReports(loadReports())
    setDashboard(seeded.dashboard)
    setHomePinnedWidgetIds(new Set(seeded.homePins.map((pin) => pin.widgetId)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reportsById = useMemo(() => new Map(reports.map((report) => [report.id, report] as const)), [reports])
  const selectedReport = widgetDraft.reportId ? (reportsById.get(widgetDraft.reportId) ?? null) : null
  const selectedDataset = selectedReport ? getLocalizedDataset(t, selectedReport.datasetKey) : null
  const datasetColumns = offeredColumns(selectedDataset?.columns ?? [], [
    widgetDraft.rowField,
    widgetDraft.columnField,
    widgetDraft.valueField,
    widgetDraft.orderField
  ])

  useEffect(() => {
    if (!selectedDataset) return
    if (widgetDraft.rowField) return

    const first = offeredColumns(selectedDataset.columns)[0]?.key
    if (first) {
      setWidgetDraft((prev) => ({ ...prev, rowField: first }))
    }
  }, [selectedDataset, widgetDraft.rowField])

  // Every report a widget (or the in-progress draft's live preview) needs
  // rows for — usePanelWidgetReportRows fetches/caches each one exactly
  // once until `ensureReportRows` (its `refetch`) is called explicitly.
  const neededReports = useMemo(() => {
    const uniqueReportIds = new Set(dashboard.widgets.map((widget) => widget.reportId))
    if (widgetDraft.reportId) uniqueReportIds.add(widgetDraft.reportId)
    return Array.from(uniqueReportIds)
      .map((reportId) => reportsById.get(reportId))
      .filter((report): report is ReportDefinitionV1 => Boolean(report))
  }, [dashboard.widgets, widgetDraft.reportId, reportsById])

  const { runStates, refetch: ensureReportRows } = usePanelWidgetReportRows(neededReports)

  const widgetList = dashboard.widgets

  const widgetsById = useMemo(
    () => new Map(dashboard.widgets.map((widget) => [widget.id, widget] as const)),
    [dashboard.widgets]
  )

  // Real drag/resize pass: derives grid position/size from each widget's
  // own `layout` field (or auto-places it, for a widget that's never been
  // dragged). Recomputed from the widget list itself rather than kept as
  // separate component state, so adding/deleting/editing a widget can never
  // leave a stale grid item behind.
  const gridLayout: Layout = useMemo(
    () => widgetList.map((widget, index) => buildLayoutItem(widget.id, index, widget.layout, widget.width)),
    [widgetList]
  )

  // Deliberately wired to onDragStop/onResizeStop, NOT onLayoutChange.
  // react-grid-layout's onLayoutChange also fires for layout recalculations
  // that have nothing to do with the operator moving anything — resizing
  // the browser window (or a monitor change) reflows/re-clamps every
  // widget's position through the same callback. Persisting from there
  // would silently overwrite a deliberately-arranged layout with whatever
  // the window happened to be doing at that moment. onDragStop/onResizeStop
  // only fire at the end of an actual completed drag or resize gesture.
  // Both use react-grid-layout's classic (i.e. pre-v2, "legacy" wrapper)
  // callback shape — (layout, oldItem, newItem, placeholder, event,
  // element) — which the package's own v2 type declarations don't reflect,
  // hence the untyped rest-args signature here (confirmed empirically:
  // logged every argument during manual testing against equipoa).
  const handleGridInteractionStop = (...args: unknown[]) => {
    const newLayout = args[0] as Layout
    if (gridBreakpoint !== GRID_BREAKPOINT_DESKTOP) return

    const changedEntries = computeChangedLayoutEntries(newLayout, (id) => widgetsById.get(id)?.layout)
    if (changedEntries.length === 0) return

    setDashboard(setWidgetLayouts(changedEntries))
  }

  const canSaveWidget = Boolean(widgetDraft.reportId) && Boolean(widgetDraft.rowField) &&
    (widgetDraft.aggregation === 'count' || Boolean(widgetDraft.valueField)) &&
    hasRequiredAdvancedFields(widgetDraft)

  const handleSaveWidget = () => {
    if (!canSaveWidget) return
    const agg = widgetDraft.aggregation
    const multiplier = Number(widgetDraft.ratioMultiplier)

    const next = upsertPivotWidget({
      id: widgetDraft.editingId ?? undefined,
      title: widgetDraft.title,
      reportId: widgetDraft.reportId,
      rowField: widgetDraft.rowField,
      rowDateGroup: widgetDraft.rowDateGroup || undefined,
      columnField: widgetDraft.columnField || undefined,
      columnDateGroup: widgetDraft.columnDateGroup || undefined,
      aggregation: widgetDraft.aggregation,
      valueField: agg === 'count' ? undefined : widgetDraft.valueField,
      // v2.2 findings.md #47: always passed explicitly — '' / null clear a
      // field that doesn't apply to the chosen aggregation.
      orderField: agg === 'last' || agg === 'sumOfLast' ? widgetDraft.orderField : '',
      entityField: agg === 'sumOfLast' ? widgetDraft.entityField : '',
      denominatorField: agg === 'ratio' ? widgetDraft.denominatorField : '',
      weightField: agg === 'weightedAvg' ? widgetDraft.weightField : '',
      ratioMultiplier: agg === 'ratio' && widgetDraft.ratioMultiplier && Number.isFinite(multiplier) ? multiplier : null,
      chartSeries: widgetDraft.columnField ? widgetDraft.chartSeries : 'total',
      showChart: widgetDraft.showChart,
      chartType: widgetDraft.chartType
    })

    setDashboard(next)
    setWidgetDraft((prev) => ({
      ...prev,
      editingId: null,
      title: '',
      columnField: '',
      rowDateGroup: '',
      columnDateGroup: '',
      valueField: '',
      chartType: 'bar',
      ...EMPTY_ADVANCED_WIDGET_DRAFT
    }))
    setIsWidgetFormOpen(false)
  }

  const handleCancelWidgetEdit = () => {
    setWidgetDraft((prev) => ({ ...prev, editingId: null, title: '', columnField: '', valueField: '', chartType: 'bar', ...EMPTY_ADVANCED_WIDGET_DRAFT }))
    setIsWidgetFormOpen(false)
  }

  return (
    // Wider than the app's default 1120px cap: a 12-column widget grid
    // benefits from the extra room a wide monitor has to give. Reports
    // also widened to this same cap (its scope choice-lists and preview
    // table benefit too) — Tasks stays at the default, still a reading-
    // width screen. Still capped, not edge-to-edge — matches the "wide
    // should be fixed, just bigger" ask.
    <PageShell style={{ maxWidth: '1800px' }}>
      <h2 className="h2">{t('dashboardNew.title')}</h2>

      <p className="lead">{t('dashboardNew.subtitle')}</p>

      {/* UX polish: the widgets are the reason operators open this screen —
          the create/edit form is a drawer trigger, not permanent on-screen
          real estate, mirroring the Lots split/merge trigger card. */}
      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding: 14,
          borderRadius: 12,
          border: '1px solid #e5e7eb',
          backgroundColor: '#ffffff',
          marginTop: 16
        }}
      >
        <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
          <strong style={{ fontSize: 14 }}>{t('dashboardNew.addWidget.title')}</strong>
          <span style={{ fontSize: 12, color: '#4b5563' }}>{t('dashboardNew.addWidget.triggerSubtitle')}</span>
        </div>
        <button
          type="button"
          className="btn-gradient"
          disabled={reports.length === 0}
          onClick={() => {
            setWidgetDraft((prev) => ({
              ...prev,
              editingId: null,
              title: '',
              columnField: '',
              rowDateGroup: '',
              columnDateGroup: '',
              valueField: '',
              chartType: 'bar',
              ...EMPTY_ADVANCED_WIDGET_DRAFT
            }))
            setIsWidgetFormOpen(true)
          }}
        >
          {t('dashboardNew.button.addWidget')}
        </button>
      </section>

      <Drawer
        open={isWidgetFormOpen}
        onClose={handleCancelWidgetEdit}
        title={widgetDraft.editingId ? t('dashboardNew.button.updateWidget') : t('dashboardNew.addWidget.title')}
      >
        {reports.length === 0 ? (
          <p className="lead">{t('dashboardNew.addWidget.empty.noReports')}</p>
        ) : (
          <>
            <div className="form-grid">
              <FormField
                id="widget-title"
                label={t('dashboardNew.form.title.label')}
                value={widgetDraft.title}
                placeholder={t('dashboardNew.form.title.placeholder')}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, title: String(value) }))}
              />

              <FormField
                id="widget-report"
                label={t('dashboardNew.form.report.label')}
                type="select"
                value={widgetDraft.reportId}
                options={reports.map((report) => ({
                  value: report.id,
                  label: `${report.name} (${getLocalizedDataset(t, report.datasetKey)?.label ?? report.datasetKey})`
                }))}
                onChange={(value) => {
                  const next = String(value)
                  const report = reportsById.get(next)
                  setWidgetDraft((prev) => ({
                    ...prev,
                    reportId: next,
                    rowField: report ? '' : prev.rowField,
                    columnField: '',
                    rowDateGroup: '',
                    columnDateGroup: '',
                    valueField: ''
                  }))
                }}
                hint={t('dashboardNew.help.reportFilter')}
              />

              <div
                data-testid="widget-breakdown-heading"
                title={t('dashboardNew.help.breakdownHint')}
                style={{ fontWeight: 700, color: '#334155', display: 'flex', gap: 6, alignItems: 'center', cursor: 'help' }}
              >
                {t('dashboardNew.help.breakdownTitle')}
                <span aria-hidden="true" style={{ fontSize: 12, color: '#64748b' }}>ⓘ</span>
              </div>

              <FormField
                id="widget-row"
                label={t('dashboardNew.form.rows.label')}
                type="select"
                value={widgetDraft.rowField}
                options={datasetColumns.map((column) => ({ value: column.key, label: column.label }))}
                hint={t('dashboardNew.help.rowField')}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, rowField: String(value), rowDateGroup: '' }))}
              />

              {widgetDraft.rowField && isDateLikeFieldKey(widgetDraft.rowField) && (
                <FormField
                  id="widget-row-date-group"
                  label={t('dashboardNew.form.rows.dateGrouping.label')}
                  type="select"
                  value={widgetDraft.rowDateGroup}
                  options={[
                    { value: '', label: t('dashboardNew.option.noneDailyRaw') },
                    { value: 'day', label: t('dashboardNew.option.day') },
                    { value: 'week', label: t('dashboardNew.option.week') },
                    { value: 'month', label: t('dashboardNew.option.month') },
                    { value: 'quarter', label: t('dashboardNew.option.quarter') },
                    { value: 'year', label: t('dashboardNew.option.year') }
                  ]}
                  onChange={(value) => setWidgetDraft((prev) => ({ ...prev, rowDateGroup: parseDateGroup(value) }))}
                />
              )}

              <FormField
                id="widget-col"
                label={t('dashboardNew.form.columns.label')}
                type="select"
                value={widgetDraft.columnField}
                options={[
                  { value: '', label: t('dashboardNew.option.none') },
                  ...datasetColumns.map((column) => ({ value: column.key, label: column.label }))
                ]}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, columnField: String(value), columnDateGroup: '' }))}
                hint={t('dashboardNew.help.columnField')}
              />

              {widgetDraft.columnField && isDateLikeFieldKey(widgetDraft.columnField) && (
                <FormField
                  id="widget-col-date-group"
                  label={t('dashboardNew.form.columns.dateGrouping.label')}
                  type="select"
                  value={widgetDraft.columnDateGroup}
                  options={[
                    { value: '', label: t('dashboardNew.option.noneDailyRaw') },
                    { value: 'day', label: t('dashboardNew.option.day') },
                    { value: 'week', label: t('dashboardNew.option.week') },
                    { value: 'month', label: t('dashboardNew.option.month') },
                    { value: 'quarter', label: t('dashboardNew.option.quarter') },
                    { value: 'year', label: t('dashboardNew.option.year') }
                  ]}
                  onChange={(value) => setWidgetDraft((prev) => ({ ...prev, columnDateGroup: parseDateGroup(value) }))}
                />
              )}

              <FormField
                id="widget-agg"
                label={t('dashboardNew.form.aggregation.label')}
                type="select"
                value={widgetDraft.aggregation}
                options={PIVOT_AGGREGATIONS.map((agg) => ({ value: agg, label: t(`dashboardNew.aggregation.${agg}`) }))}
                hint={t(`dashboardNew.aggregation.help.${widgetDraft.aggregation}`)}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, aggregation: String(value) as PivotAggregation }))}
              />

              <FormField
                id="widget-value"
                label={t('dashboardNew.form.valueField.label')}
                type="select"
                value={widgetDraft.valueField}
                options={[
                  {
                    value: '',
                    label: widgetDraft.aggregation === 'count'
                      ? t('dashboardNew.form.valueField.placeholder.count')
                      : t('dashboardNew.form.valueField.placeholder.select')
                  },
                  ...datasetColumns.map((column) => ({ value: column.key, label: column.label }))
                ]}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, valueField: String(value) }))}
              />

              {widgetDraft.aggregation === 'sumOfLast' && (
                <FormField
                  id="widget-entity"
                  label={t('dashboardNew.form.entityField.label')}
                  type="select"
                  value={widgetDraft.entityField}
                  options={[
                    { value: '', label: t('dashboardNew.form.valueField.placeholder.select') },
                    ...datasetColumns.map((column) => ({ value: column.key, label: column.label }))
                  ]}
                  hint={t('dashboardNew.form.entityField.hint')}
                  onChange={(value) => setWidgetDraft((prev) => ({ ...prev, entityField: String(value) }))}
                />
              )}

              {(widgetDraft.aggregation === 'last' || widgetDraft.aggregation === 'sumOfLast') && (
                <FormField
                  id="widget-order"
                  label={t('dashboardNew.form.orderField.label')}
                  type="select"
                  value={widgetDraft.orderField}
                  options={[
                    { value: '', label: t('dashboardNew.form.orderField.auto') },
                    ...datasetColumns
                      .filter((column) => isDateLikeFieldKey(column.key))
                      .map((column) => ({ value: column.key, label: column.label }))
                  ]}
                  hint={t('dashboardNew.form.orderField.hint')}
                  onChange={(value) => setWidgetDraft((prev) => ({ ...prev, orderField: String(value) }))}
                />
              )}

              {widgetDraft.aggregation === 'ratio' && (
                <>
                  <FormField
                    id="widget-denominator"
                    label={t('dashboardNew.form.denominatorField.label')}
                    type="select"
                    value={widgetDraft.denominatorField}
                    options={[
                      { value: '', label: t('dashboardNew.form.valueField.placeholder.select') },
                      ...datasetColumns.map((column) => ({ value: column.key, label: column.label }))
                    ]}
                    onChange={(value) => setWidgetDraft((prev) => ({ ...prev, denominatorField: String(value) }))}
                  />
                  <FormField
                    id="widget-ratio-multiplier"
                    label={t('dashboardNew.form.ratioMultiplier.label')}
                    type="select"
                    value={widgetDraft.ratioMultiplier}
                    options={[
                      { value: '', label: t('dashboardNew.form.ratioMultiplier.none') },
                      { value: '100', label: t('dashboardNew.form.ratioMultiplier.percent') },
                      { value: '1000', label: t('dashboardNew.form.ratioMultiplier.perMille') }
                    ]}
                    onChange={(value) => setWidgetDraft((prev) => ({ ...prev, ratioMultiplier: String(value) }))}
                  />
                </>
              )}

              {widgetDraft.aggregation === 'weightedAvg' && (
                <FormField
                  id="widget-weight"
                  label={t('dashboardNew.form.weightField.label')}
                  type="select"
                  value={widgetDraft.weightField}
                  options={[
                    { value: '', label: t('dashboardNew.form.valueField.placeholder.select') },
                    ...datasetColumns.map((column) => ({ value: column.key, label: column.label }))
                  ]}
                  hint={t('dashboardNew.form.weightField.hint')}
                  onChange={(value) => setWidgetDraft((prev) => ({ ...prev, weightField: String(value) }))}
                />
              )}
            </div>

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, fontWeight: 600 }}>
              <input
                type="checkbox"
                checked={widgetDraft.showChart}
                onChange={(event) => setWidgetDraft((prev) => ({ ...prev, showChart: event.target.checked }))}
              />
              <span>{t('dashboardNew.form.showChart')}</span>
            </label>

            {widgetDraft.showChart && (
              <FormField
                id="widget-chart-type"
                label={t('dashboardNew.form.chartType.label')}
                type="select"
                value={widgetDraft.chartType}
                options={[
                  { value: 'bar', label: t('dashboardNew.chartType.bar') },
                  { value: 'line', label: t('dashboardNew.chartType.line') }
                ]}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, chartType: value === 'line' ? 'line' : 'bar' }))}
              />
            )}

            {widgetDraft.showChart && widgetDraft.columnField && (
              <FormField
                id="widget-chart-series"
                label={t('dashboardNew.form.chartSeries.label')}
                type="select"
                value={widgetDraft.chartSeries}
                options={[
                  { value: 'total', label: t('dashboardNew.form.chartSeries.total') },
                  { value: 'byColumn', label: t('dashboardNew.form.chartSeries.byColumn') }
                ]}
                onChange={(value) => setWidgetDraft((prev) => ({ ...prev, chartSeries: value === 'byColumn' ? 'byColumn' : 'total' }))}
              />
            )}

            <p className="lead" style={{ marginTop: 10 }}>
              {t('dashboardNew.help.datasetAllowlist', { count: String(DATASETS.length) })}
            </p>

            <div style={{ marginTop: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-gradient"
                disabled={!canSaveWidget}
                onClick={handleSaveWidget}
              >
                {widgetDraft.editingId ? t('dashboardNew.button.updateWidget') : t('dashboardNew.button.addWidget')}
              </button>
              <button type="button" onClick={handleCancelWidgetEdit}>
                {t('common.cancel')}
              </button>
            </div>
          </>
        )}
      </Drawer>

      <div className="form-card" style={{ marginTop: 16 }}>
        <h3 className="h2" style={{ marginTop: 0 }}>{t('dashboardNew.widgets.title')}</h3>

        {widgetList.length === 0 ? (
          <p className="lead">{t('dashboardNew.widgets.empty')}</p>
        ) : (
          <ResponsiveGridLayout
            className={styles.widgetGrid}
            layouts={{ [GRID_BREAKPOINT_DESKTOP]: gridLayout }}
            breakpoints={GRID_BREAKPOINTS}
            cols={GRID_COLS}
            rowHeight={GRID_ROW_HEIGHT}
            margin={[14, 14]}
            containerPadding={[0, 0]}
            isDraggable={gridBreakpoint === GRID_BREAKPOINT_DESKTOP}
            isResizable={gridBreakpoint === GRID_BREAKPOINT_DESKTOP}
            draggableHandle={`.${styles.widgetDragHandle}`}
            onBreakpointChange={(next) => setGridBreakpoint(next)}
            onDragStop={handleGridInteractionStop}
            onResizeStop={handleGridInteractionStop}
          >
            {widgetList.map((widget) => {
              const report = reportsById.get(widget.reportId)
              const runState = runStates[widget.reportId]

              return (
                <div key={widget.id}>
                  <PanelWidgetCard
                    widget={widget}
                    report={report}
                    runState={runState}
                    draggable
                    actions={
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            const reportForEdit = reportsById.get(widget.reportId)
                            setWidgetDraft({
                              editingId: widget.id,
                              title: widget.title,
                              reportId: widget.reportId,
                              rowField: widget.rowField,
                              columnField: widget.columnField ?? '',
                              rowDateGroup: widget.rowDateGroup ?? '',
                              columnDateGroup: widget.columnDateGroup ?? '',
                              aggregation: widget.aggregation,
                              valueField: widget.valueField ?? '',
                              showChart: widget.showChart,
                              chartType: widget.chartType ?? 'bar',
                              orderField: widget.orderField ?? '',
                              entityField: widget.entityField ?? '',
                              denominatorField: widget.denominatorField ?? '',
                              weightField: widget.weightField ?? '',
                              ratioMultiplier: widget.ratioMultiplier !== undefined ? String(widget.ratioMultiplier) : '',
                              chartSeries: widget.chartSeries ?? 'total'
                            })
                            setIsWidgetFormOpen(true)

                            if (reportForEdit) {
                              void ensureReportRows(reportForEdit)
                            }
                          }}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const next = deleteWidget(widget.id)
                            setDashboard(next)
                          }}
                        >
                          {t('common.delete')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const pinned = homePinnedWidgetIds.has(widget.id)
                            setHomeWidgetPinned(widget.id, !pinned)
                            setHomePinnedWidgetIds((prev) => {
                              const next = new Set(prev)
                              if (pinned) next.delete(widget.id)
                              else next.add(widget.id)
                              return next
                            })
                          }}
                        >
                          {homePinnedWidgetIds.has(widget.id) ? t('dashboardNew.button.unpinFromHome') : t('dashboardNew.button.pinToHome')}
                        </button>
                        {report && (
                          <button
                            type="button"
                            onClick={() => {
                              void ensureReportRows(report)
                            }}
                          >
                            {t('dashboardNew.button.refresh')}
                          </button>
                        )}
                      </>
                    }
                  />
                </div>
              )
            })}
          </ResponsiveGridLayout>
        )}
      </div>
    </PageShell>
  )
}
